import { User } from 'redux/types/user';

// Single source of truth for the browser session, shared by every tab of this origin.
// Only the tab that produces a change (login, logout, switch, refresh) writes; the rest read and
// adopt via the `storage` event (fired in the other tabs, never in the writer). That is what keeps
// a stale tab from overwriting a newer session — the root of the multi-tab logouts we used to get
// while `user` lived inside the shared redux-persist blob.

export const SESSION_STORAGE_KEY = 'lidz-session';
const LEGACY_PERSIST_KEY = 'persist:root-lidz';

const hasStorage = (): boolean => typeof window !== 'undefined' && Boolean(window.localStorage);

function parseSession(raw: string | null): User | undefined {
  if (!raw) return undefined;
  try {
    const user = JSON.parse(raw) as User;
    return user.accessToken ? user : undefined;
  } catch {
    return undefined;
  }
}

// TODO: remove once every browser has booted at least once after the sessionStore release.
// Seeds this store from the legacy blob so nobody is logged out by the deploy, and drops `user`
// from the blob so it can never shadow this store again.
function migrateLegacySession(): User | undefined {
  try {
    const raw = localStorage.getItem(LEGACY_PERSIST_KEY);
    if (!raw) return undefined;
    const blob = JSON.parse(raw) as Record<string, unknown>;
    if (typeof blob.user !== 'string') return undefined;

    const user = parseSession(blob.user);
    delete blob.user;
    localStorage.setItem(LEGACY_PERSIST_KEY, JSON.stringify(blob));
    if (user) localStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify(user));
    return user;
  } catch {
    return undefined;
  }
}

export function readSession(): User | undefined {
  if (!hasStorage()) return undefined;
  return parseSession(localStorage.getItem(SESSION_STORAGE_KEY)) ?? migrateLegacySession();
}

export function writeSession(session: User): void {
  if (hasStorage()) localStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify(session));
}

export function clearSession(): void {
  if (hasStorage()) localStorage.removeItem(SESSION_STORAGE_KEY);
}

// `null` means another tab logged out.
export function subscribeToSession(listener: (session: User | null) => void): () => void {
  if (typeof window === 'undefined') return (): void => {};

  const handleStorage = (event: StorageEvent): void => {
    if (event.key !== SESSION_STORAGE_KEY) return;
    if (event.newValue === null) listener(null);
    else {
      const session = parseSession(event.newValue);
      if (session) listener(session);
    }
  };

  window.addEventListener('storage', handleStorage);
  return (): void => window.removeEventListener('storage', handleStorage);
}

// Set while this tab shows the "another tab switched project" prompt. The API layer reads it so a
// 401 from the now-stale session waits for the user instead of refreshing or signing out.
let switchPending = false;

export const isSwitchPending = (): boolean => switchPending;

export const markSwitchPending = (): void => {
  switchPending = true;
};
